import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './ReminderListTable.module.scss';
import { DataTableWithSidebar, DataTableColumn, OrderData } from 'common/components/dataTable';
import { ReminderListItemDto } from 'api/reminders/models/ReminderListItemDto';
import { RemindersSearchCriteria } from 'api/reminders/models/RemindersSearchCriteria';
import { DATE_FORMAT_DEFAUT, DefaultAssetImage, LOGGER_LOG_TYPE } from 'Config';
import Badge from 'common/components/badge/Badge';
import QuestionYesNo from 'common/components/questionYesNo/QuestionYesNo';
import Dropdown from 'common/components/dropdown/Dropdown';
import DropdownItem from 'common/components/dropdown/DropdownItem';
import { UserProfile } from 'api/account/models/UserProfile';
import UsersService from 'api/users/UsersService';
import { useSelector } from 'react-redux';
import { Reducers } from 'store/types';
import { FaEllipsisH } from 'react-icons/fa';
import { useParams } from 'react-router-dom';
import DateFormat from 'common/components/dateFormat/dateFormat';
import RemindersService from 'api/reminders/RemindersService';
import Loading from 'common/services/Loading';
import Logger from 'common/services/Logger';
import { useToasts } from 'react-toast-notifications';
import ReactTooltip from 'react-tooltip';
import moment from 'moment';

type ReminderTableRow = ReminderListItemDto & {
    vehicleRegistration: string;
    vehicleBrand: string;
    vehicleModel: string;
    vehicleImage: string;
    categoryDisplay: string;
    criteriaValueDisplay: string;
    criteriaTypeDisplay: string;
    alertBeforeDisplay: string;
    alertRepeatDisplay: string;
    alertStopDisplay: string;
    alertInDisplay: string;
    contactsDisplay: string;
    statusDisplay: string;
    vehicleSegmentDisplay: string;
    vehicleLocalDisplay: string;
};

export type Props = {
    criteria?: RemindersSearchCriteria;
    editable: boolean;
    items: ReminderListItemDto[];
    totalItems: number;
    isLoadingMore: boolean;
    hasMore: boolean;
    onTableFilter: (field: string, isFilterAsc: boolean) => void;
    onLoadMore: () => void;
    onClickItem: (item: ReminderListItemDto, event: any) => void;
    onDeleteFromList?: () => void;
    reRenderSearch: number;
    scrollToRowId?: string | null;
    hasMorePrevious?: boolean;
    isLoadingPrevious?: boolean;
    onLoadPrevious?: () => void;
    onFetchAllForExport?: () => Promise<ReminderListItemDto[]>;
    exportRef?: React.MutableRefObject<{ exportExcel: () => Promise<void> } | null>;
};

const ReminderListTable = ({
    criteria,
    items,
    isLoadingMore,
    hasMore,
    onTableFilter,
    onLoadMore,
    onClickItem,
    onDeleteFromList,
    reRenderSearch,
    scrollToRowId,
    hasMorePrevious,
    isLoadingPrevious,
    onLoadPrevious,
    onFetchAllForExport,
    exportRef,
}: Props) => {
    const { t } = useTranslation();
    const { addToast } = useToasts();
    const { vehicleId } = useParams<{ vehicleId: string }>();

    const imageCacheKey = useRef(new Date().getTime());
    const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const hasRemindersWritePolicy = UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_REMINDER_WRITE']);

    const [itemToRemove, setItemToRemove] = useState<ReminderListItemDto | null>(null);
    const [showRemoveModal, setShowRemoveModal] = useState(false);

    const showRemoveItemDialog = (item: ReminderListItemDto) => {
        setItemToRemove(item);
        setShowRemoveModal(true);
    };

    const onCancelRemove = () => {
        setItemToRemove(null);
        setShowRemoveModal(false);
    };

    const onRemove = async () => {
        if (itemToRemove === null) {
            addToast(t('common.messages.record_delete_error'), { appearance: 'error' });
            return;
        }

        try {
            setShowRemoveModal(false);
            Loading.show();
            await RemindersService.remove(itemToRemove.id);
            onCancelRemove();
            if (onDeleteFromList) onDeleteFromList();
            addToast(t('common.messages.record_delete_success'), { appearance: 'success' });
            Loading.hide();
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't delete reminder`, error);
            addToast(t('common.messages.record_delete_error'), { appearance: 'error' });
            Loading.hide();
        }
    };

    const finish = async (row: ReminderListItemDto) => {
        try {
            Loading.show();
            await RemindersService.finish(row.triggerId);
            addToast(t('common.messages.record_finish_success'), { appearance: 'success' });
            if (onDeleteFromList) onDeleteFromList();
            Loading.hide();
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't finish reminder`, error);
            addToast(t('common.messages.record_finish_error'), { appearance: 'error' });
            Loading.hide();
        }
    };

    const repeatLabel = (value: number, interval: string) => {
        return `${value} ` + t(`reminders.list.${interval}${value > 1 ? '_PLURAL' : '_SINGLE'}` as any);
    };

    const renderCriteriaValueCell = (row: ReminderTableRow) => {
        return (
            <div className={styles.cellContainer}>
                {row.criteria === 'DATE' && <div className={styles.firstRow}>
                    <DateFormat value={row.firstDate ?? new Date()} />
                    {row.firstDate && (() => { const d = new Date(row.firstDate); return (d.getHours() !== 0 || d.getMinutes() !== 0) ? ` ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : ''; })()}
                </div>}
                {row.criteria === 'KMS' && <div className={styles.firstRow}>{repeatLabel(row.firstKm ?? 0, 'KMS')}</div>}
                {row.criteria === 'HOURS' && <div className={styles.firstRow}>{repeatLabel(row.firstHour ?? 0, 'HOURS')}</div>}
            </div>
        );
    };

    const renderCriteriaTypeCell = (row: ReminderTableRow) => {
        return <>{t(`reminders.notifications.criteria.${row.criteria}` as any)}</>;
    };

    const renderBeforeCell = (row: ReminderTableRow) => {
        return (
            <div className={styles.cellContainer}>
                {row.criteria === 'DATE' && row.beforeDateValue &&
                    <div className={styles.firstRow}>{repeatLabel(row.beforeDateValue, row.beforeDateMeasure)}</div>}
                {row.criteria === 'KMS' && row.beforeKm &&
                    <div className={styles.firstRow}>{repeatLabel(row.beforeKm, 'KMS')}</div>}
                {row.criteria === 'HOURS' && row.beforeHour &&
                    <div className={styles.firstRow}>{repeatLabel(row.beforeHour, 'HOURS')}</div>}
            </div>
        );
    };

    const renderRepeatCell = (row: ReminderTableRow) => {
        return (
            <div className={styles.cellContainer}>
                {row.criteria === 'DATE' && row.repeatDateValue &&
                    <div className={styles.firstRow}>{repeatLabel(row.repeatDateValue, row.repeatDateMeasure)}</div>}
                {row.criteria === 'KMS' && row.repeatKm &&
                    <div className={styles.firstRow}>{repeatLabel(row.repeatKm, 'KMS')}</div>}
                {row.criteria === 'HOURS' && row.repeatHour &&
                    <div className={styles.firstRow}>{repeatLabel(row.repeatHour, 'HOURS')}</div>}
            </div>
        );
    };

    const renderStopCell = (row: ReminderTableRow) => {
        return (
            <div className={styles.cellContainer}>
                {row.criteria === 'DATE' && row.stopDate &&
                    <div className={styles.firstRow}><DateFormat value={row.stopDate} /></div>}
                {row.criteria === 'KMS' && row.stopKm &&
                    <div className={styles.firstRow}>{repeatLabel(row.stopKm, 'KMS')}</div>}
                {row.criteria === 'HOURS' && row.stopHour &&
                    <div className={styles.firstRow}>{repeatLabel(row.stopHour, 'HOURS')}</div>}
            </div>
        );
    };

    const renderEmailCell = (row: ReminderTableRow) => {
        if (!row.contacts || row.contacts.length === 0) return <></>;

        const maxVisible = 2;
        const visible = row.contacts.slice(0, maxVisible);
        const remaining = row.contacts.slice(maxVisible);

        return (
            <div className={styles.contactsCell}>
                {visible.map(x => <span key={`contact_${x.id}`}>{x.email}</span>)}
                {remaining.length > 0 && (
                    <div className={styles.contactsOverflow}>
                        <span className={styles.contactsMoreBadge}>+{remaining.length}</span>
                        <div className={styles.contactsTooltip}>
                            {remaining.map(x => <div key={`contact_extra_${x.id}`}>{x.email}</div>)}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'EXPIRED': return '#DC4C4C';
            case 'ACTIVE': return '#56CF76';
            case 'FINISHED': return '#1781A1';
            default: return '#1781A1';
        }
    };

    const renderTableActionCell = (row: ReminderTableRow) => {
        let urlDetails = `reminders/details/${row.id}`;
        let urlEdit = `reminders/edit/${row.id}`;

        if (row.vehicleMaintenanceId && row.vehicleId) {
            urlDetails = `/vehicles/details/${row.vehicleId}/maintenance/details/${row.vehicleMaintenanceId}`;
            urlEdit = `/vehicles/details/${row.vehicleId}/maintenance/edit/${row.vehicleMaintenanceId}`;
        } else if (row.useContractId && row.vehicleId) {
            urlDetails = `/vehicles/details/${row.vehicleId}/contract/details/use/details/${row.useContractId}`;
            urlEdit = `/vehicles/details/${row.vehicleId}/contract/details/use/edit/${row.useContractId}`;
        } else if (row.maintenanceContractId && row.vehicleId) {
            urlDetails = `/vehicles/details/${row.vehicleId}/contract/details/maintenance/details/${row.maintenanceContractId}`;
            urlEdit = `/vehicles/details/${row.vehicleId}/contract/details/maintenance/edit/${row.maintenanceContractId}`;
        } else if (row.insuranceContractId && row.vehicleId) {
            urlDetails = `/vehicles/details/${row.vehicleId}/contract/details/insurance/details/${row.insuranceContractId}`;
            urlEdit = `/vehicles/details/${row.vehicleId}/contract/details/insurance/edit/${row.insuranceContractId}`;
        }

        return (
            <Dropdown
                options={
                    <>
                        <DropdownItem url={urlDetails}>
                            {t('common.details')}
                        </DropdownItem>
                        {hasRemindersWritePolicy && <DropdownItem url={urlEdit}>
                            {t('common.edit')}
                        </DropdownItem>}
                        {!row.vehicleMaintenanceId && !row.useContractId && !row.maintenanceContractId && !row.insuranceContractId && hasRemindersWritePolicy && <DropdownItem onClick={() => showRemoveItemDialog(row)}>
                            {t('common.remove')}
                        </DropdownItem>}
                        {row.status === 'ACTIVE' && hasRemindersWritePolicy && <DropdownItem onClick={() => finish(row)}>
                            {t('common.finish')}
                        </DropdownItem>}
                    </>
                }
            >
                <div>
                    <FaEllipsisH />
                </div>
            </Dropdown>
        );
    };

    const getExportCriteriaValue = (row: ReminderTableRow): string => {
        if (row.criteria === 'DATE') {
            if (!row.firstDate) return '';
            const d = new Date(row.firstDate);
            const hasTime = d.getHours() !== 0 || d.getMinutes() !== 0;
            return hasTime ? moment(row.firstDate).format(`${DATE_FORMAT_DEFAUT} HH:mm`) : moment(row.firstDate).format(DATE_FORMAT_DEFAUT);
        }
        if (row.criteria === 'KMS') return repeatLabel(row.firstKm ?? 0, 'KMS');
        if (row.criteria === 'HOURS') return repeatLabel(row.firstHour ?? 0, 'HOURS');
        return '';
    };

    const getExportBeforeValue = (row: ReminderTableRow): string => {
        if (row.criteria === 'DATE' && row.beforeDateValue) return repeatLabel(row.beforeDateValue, row.beforeDateMeasure);
        if (row.criteria === 'KMS' && row.beforeKm) return repeatLabel(row.beforeKm, 'KMS');
        if (row.criteria === 'HOURS' && row.beforeHour) return repeatLabel(row.beforeHour, 'HOURS');
        return '';
    };

    const getExportRepeatValue = (row: ReminderTableRow): string => {
        if (row.criteria === 'DATE' && row.repeatDateValue) return repeatLabel(row.repeatDateValue, row.repeatDateMeasure);
        if (row.criteria === 'KMS' && row.repeatKm) return repeatLabel(row.repeatKm, 'KMS');
        if (row.criteria === 'HOURS' && row.repeatHour) return repeatLabel(row.repeatHour, 'HOURS');
        return '';
    };

    const getExportStopValue = (row: ReminderTableRow): string => {
        if (row.criteria === 'DATE' && row.stopDate) return moment(row.stopDate).format(DATE_FORMAT_DEFAUT);
        if (row.criteria === 'KMS' && row.stopKm) return repeatLabel(row.stopKm, 'KMS');
        if (row.criteria === 'HOURS' && row.stopHour) return repeatLabel(row.stopHour, 'HOURS');
        return '';
    };

    const formatTimeRemaining = (totalHours: number): string => {
        if (totalHours < 0) return t('reminders.list.alert_overdue') as string;
        const days = Math.floor(totalHours / 24);
        const hours = Math.floor(totalHours % 24);
        if (days === 0 && hours === 0) return t('reminders.list.today') as string;
        if (days === 0) return `${hours} ${t(`reminders.list.HOURS${hours > 1 ? '_PLURAL' : '_SINGLE'}` as any)}`;
        if (days < 30) {
            const dayLabel = `${days} ${t(`reminders.list.DAY${days > 1 ? '_PLURAL' : '_SINGLE'}` as any)}`;
            if (hours > 0) return `${dayLabel} ${hours}h`;
            return dayLabel;
        }
        const months = Math.floor(days / 30);
        const remainingDays = days % 30;
        if (months < 12) {
            const monthLabel = `${months} ${t(`reminders.list.MONTH${months > 1 ? '_PLURAL' : '_SINGLE'}` as any)}`;
            if (remainingDays > 0) return `${monthLabel} ${remainingDays} ${t(`reminders.list.DAY${remainingDays > 1 ? '_PLURAL' : '_SINGLE'}` as any)}`;
            return monthLabel;
        }
        const years = Math.floor(months / 12);
        const remainingMonths = months % 12;
        const yearLabel = `${years} ${t(`reminders.list.YEAR${years > 1 ? '_PLURAL' : '_SINGLE'}` as any)}`;
        if (remainingMonths > 0) return `${yearLabel} ${remainingMonths} ${t(`reminders.list.MONTH${remainingMonths > 1 ? '_PLURAL' : '_SINGLE'}` as any)}`;
        return yearLabel;
    };

    // What's left until a km/hours trigger alerts (null when not a meter trigger or data is missing)
    const getMeterRemaining = (row: ReminderTableRow): { remaining: number; label: string; tooltip: string } | null => {
        if (row.criteria === 'KMS' && row.nextKm != null && row.vehicleCurrentKilometers != null) {
            const remaining = row.nextKm - row.vehicleCurrentKilometers;
            return {
                remaining,
                label: `${remaining.toLocaleString()} km`,
                tooltip: t('reminders.list.tooltip_km', { km: row.nextKm.toLocaleString() }) as string,
            };
        }
        if (row.criteria === 'HOURS' && row.nextHour != null && row.vehicleCurrentHours != null) {
            const remaining = row.nextHour - row.vehicleCurrentHours;
            return {
                remaining,
                label: `${remaining.toLocaleString(undefined, { maximumFractionDigits: 1 })} ${t('common.hours_symbol')}`,
                tooltip: t('reminders.list.tooltip_hours', { hours: row.nextHour.toLocaleString() }) as string,
            };
        }
        return null;
    };

    const renderAlertInCell = (row: ReminderTableRow) => {
        const tooltipId = `alert_in_tooltip_${row.triggerId ?? row.id}`;
        if (row.status === 'FINISHED' || row.status === 'EXPIRED') {
            return (
                <div className={styles.cellContainer}>
                    <div className={styles.alertSent}>{t('reminders.list.alert_sent')}</div>
                </div>
            );
        }
        if (row.criteria === 'DATE' && row.nextDate) {
            const now = moment();
            const next = moment(row.nextDate);
            const diffHours = next.diff(now, 'hours');
            if (diffHours < 0) {
                return (
                    <div className={styles.cellContainer}>
                        <div className={styles.alertSent}>{t('reminders.list.alert_sent')}</div>
                    </div>
                );
            }
            const label = formatTimeRemaining(diffHours);
            const exactDate = next.format(DATE_FORMAT_DEFAUT);
            const tooltipText = t('reminders.list.tooltip_date', { date: exactDate }) as string;
            return (
                <div className={styles.cellContainer}>
                    <div className={styles.firstRow} data-tip={tooltipText} data-for={tooltipId}>
                        {label}
                    </div>
                    <ReactTooltip id={tooltipId} effect="solid" />
                </div>
            );
        }
        const meter = getMeterRemaining(row);
        if (meter) {
            if (meter.remaining <= 0) {
                return (
                    <div className={styles.cellContainer}>
                        <div className={styles.alertSent}>{t('reminders.list.alert_sent')}</div>
                    </div>
                );
            }
            return (
                <div className={styles.cellContainer}>
                    <div className={styles.firstRow} data-tip={meter.tooltip} data-for={tooltipId}>
                        {meter.label}
                    </div>
                    <ReactTooltip id={tooltipId} effect="solid" />
                </div>
            );
        }
        return <></>;
    };

    const getExportAlertInValue = (row: ReminderTableRow): string => {
        if (row.status === 'FINISHED' || row.status === 'EXPIRED') return t('reminders.list.alert_sent') as string;
        if (row.criteria === 'DATE' && row.nextDate) {
            const diffHours = moment(row.nextDate).diff(moment(), 'hours');
            if (diffHours < 0) return t('reminders.list.alert_sent') as string;
            return `${formatTimeRemaining(diffHours)}`;
        }
        const meter = getMeterRemaining(row);
        if (meter) {
            if (meter.remaining <= 0) return t('reminders.list.alert_sent') as string;
            return meter.label;
        }
        return '';
    };

    const tableColumns: DataTableColumn<ReminderTableRow>[] = useMemo(() => {
        const cols: DataTableColumn<ReminderTableRow>[] = [
            {
                field: 'categoryDisplay',
                name: t('reminders.list.category') as string,
                orderColumn: 'rc.designation',
                width: 150,
                exportValue: row => row.categoryIsSystem ? t(`reminder_categories.category_types.${row.categoryDesignation}` as any) as string : row.categoryDesignation,
                renderCell: row =>
                    <>{row.categoryIsSystem ? t(`reminder_categories.category_types.${row.categoryDesignation}` as any) : row.categoryDesignation}</>,
            },
            {
                field: 'criteriaTypeDisplay',
                name: t('reminders.list.criteria_type') as string,
                orderColumn: 'rt.criteria',
                width: 80,
                hideOn: ['sm', 'md'],
                exportValue: row => row.criteria ? t(`reminders.notifications.criteria.${row.criteria}` as any) as string : '',
                renderCell: renderCriteriaTypeCell,
            },
            {
                field: 'criteriaValueDisplay',
                name: t('reminders.list.first_reminder') as string,
                orderColumn: 'rt.first_date',
                width: 120,
                hideOn: ['sm', 'md'],
                exportValue: row => getExportCriteriaValue(row),
                renderCell: renderCriteriaValueCell,
            },
            {
                field: 'alertBeforeDisplay',
                name: t('reminders.list.alert_before') as string,
                hideOrderButton: true,
                width: 100,
                hideOn: ['sm', 'md'],
                exportValue: row => getExportBeforeValue(row),
                renderCell: renderBeforeCell,
            },
            {
                field: 'alertRepeatDisplay',
                name: t('reminders.list.alert_repeat') as string,
                hideOrderButton: true,
                width: 100,
                hideOn: ['sm', 'md'],
                exportValue: row => getExportRepeatValue(row),
                renderCell: renderRepeatCell,
            },
            {
                field: 'alertStopDisplay',
                name: t('reminders.list.alert_stop') as string,
                hideOrderButton: true,
                width: 100,
                exportValue: row => getExportStopValue(row),
                renderCell: renderStopCell,
            },
            {
                field: 'alertInDisplay',
                name: t('reminders.list.alert_in') as string,
                hideOrderButton: true,
                width: 140,
                hideOn: ['sm', 'md'],
                exportValue: row => getExportAlertInValue(row),
                renderCell: renderAlertInCell,
            },
            {
                field: 'contactsDisplay',
                name: t('reminders.list.contacts') as string,
                hideOrderButton: true,
                width: 200,
                hideOn: ['sm', 'md', 'lg', 'xl'],
                exportValue: row => row.contacts && row.contacts.length > 0 ? row.contacts.map(c => c.email).join(', ') : '',
                renderCell: renderEmailCell,
                cellStyle: { overflow: 'visible' },
            },
            {
                field: 'statusDisplay',
                name: t('reminders.list.status') as string,
                orderColumn: 'rt.status',
                width: 100,
                exportValue: row => row.status ? t(`reminders.notifications.status.${row.status}` as any) as string : '',
                renderCell: row => (
                    <>
                        <Badge backgroundColor={getStatusColor(row.status)} data-tip={t(`reminders.list.status_tooltip.${row.status}` as any)} data-for={'status_tooltip_' + row.id}>
                            {row.status ? t(`reminders.notifications.status.${row.status}` as any) : ''}
                        </Badge>
                        <ReactTooltip id={'status_tooltip_' + row.id} />
                    </>
                ),
            },
        ];

        if (!vehicleId) {
            cols.unshift(
                {
                    field: 'vehicleImage',
                    name: t('common.image') as string,
                    hideOrderButton: true,
                    width: 75,
                    hideOn: ['sm', 'md'],
                    excludeFromExport: true,
                    renderCell: row =>
                        <div className={styles.imageDescCell}>
                            <div className={styles.contentImage}>
                                <div className={styles.img} style={{ width: '5rem', height: '3rem', backgroundImage: row?.vehiclePhotoUrl ? 'url(' + row?.vehiclePhotoUrl + '?_=' + imageCacheKey.current + ')' : row?.vehicleSegmentDefaultPhotoUrl ? 'url(' + row?.vehicleSegmentDefaultPhotoUrl + '?_=' + imageCacheKey.current + ')' : 'url(' + DefaultAssetImage + ')' }} />
                            </div>
                        </div>,
                },
                {
                    field: 'vehicleRegistration',
                    name: t('reminders.list.vehicle') as string,
                    orderColumn: 'vehicles.registration_number',
                    width: 130,
                    renderCell: row =>
                        <div className={styles.descCell}>
                            <div className={styles.columnPlate}>
                                {row.vehicleRegistrationNumber}
                            </div>
                        </div>,
                },
                {
                    field: 'vehicleBrand',
                    name: t('reminders.list.brand') as string,
                    orderColumn: 'vb.name',
                    width: 100,
                    hideOn: ['sm'],
                    renderCell: row =>
                        <div className={styles.columnBrand}>
                            {row.vehicleBrandName || '-'}
                        </div>,
                },
                {
                    field: 'vehicleModel',
                    name: t('reminders.list.model') as string,
                    orderColumn: 'vm.name',
                    width: 100,
                    hideOn: ['sm'],
                    renderCell: row =>
                        <div className={styles.columnBrand}>
                            {row.vehicleModelName || '-'}
                        </div>,
                },
                {
                    field: 'vehicleSegmentDisplay',
                    name: t('vehicle.type') as string,
                    orderColumn: 'vehicle_segments.name',
                    width: 130,
                    hideOn: ['sm', 'md', 'lg'],
                    exportValue: row => row.vehicleSegmentName || '-',
                    renderCell: row => <>{row.vehicleSegmentName || '-'}</>,
                },
                {
                    field: 'vehicleLocalDisplay',
                    name: t('vehicle.local') as string,
                    hideOrderButton: true,
                    width: 130,
                    hideOn: ['sm', 'md', 'lg'],
                    exportValue: row => row.vehicleLocalNames || '-',
                    renderCell: row => <>{row.vehicleLocalNames || '-'}</>,
                },
            );
        }

        return cols;
    }, [vehicleId]);

    const toTableRow = (item: ReminderListItemDto): ReminderTableRow => ({
        ...item,
        vehicleRegistration: item.vehicleRegistrationNumber || '',
        vehicleBrand: item.vehicleBrandName || '',
        vehicleModel: item.vehicleModelName || '',
        vehicleImage: item.vehiclePhotoUrl || '',
        categoryDisplay: item.categoryDesignation || '',
        criteriaValueDisplay: item.criteria || '',
        criteriaTypeDisplay: item.criteria || '',
        alertBeforeDisplay: '',
        alertRepeatDisplay: '',
        alertStopDisplay: '',
        alertInDisplay: '',
        contactsDisplay: (item.contacts || []).map(c => c.email).join(', '),
        statusDisplay: item.status || '',
        vehicleSegmentDisplay: item.vehicleSegmentName || '',
        vehicleLocalDisplay: item.vehicleLocalNames || '',
    });

    const tableRows: ReminderTableRow[] = useMemo(() => {
        return items.map(toTableRow);
    }, [items]);

    const fetchAllForExcelExport = useCallback(async (): Promise<ReminderTableRow[]> => {
        if (!onFetchAllForExport) return [];
        const allReminders = await onFetchAllForExport();
        return allReminders.map(toTableRow);
    }, [onFetchAllForExport]);

    const handleOrderChange = (orderData: OrderData<ReminderTableRow>) => {
        const orderColumn = orderData.orderColumn || (orderData.field as string);
        onTableFilter(orderColumn, orderData.isOrderAsc);
    };

    const getFieldFromOrderColumn = (orderColumn: string | undefined): keyof ReminderTableRow => {
        const mapping: Record<string, keyof ReminderTableRow> = {
            'vehicles.registration_number': 'vehicleRegistration',
            'vb.name': 'vehicleBrand',
            'vm.name': 'vehicleModel',
            'rc.designation': 'categoryDisplay',
            'rt.first_date': 'criteriaValueDisplay',
            'rt.criteria': 'criteriaTypeDisplay',
            'rt.status': 'statusDisplay',
            'r.created_date': 'categoryDisplay',
            'vehicle_segments.name': 'vehicleSegmentDisplay',
        };
        return mapping[orderColumn ?? ''] ?? 'categoryDisplay';
    };

    const defaultOrder: OrderData<ReminderTableRow> = useMemo(() => {
        const orderColumn = criteria?.orderColumn ?? 'r.created_date';
        return {
            field: getFieldFromOrderColumn(orderColumn),
            orderColumn: orderColumn,
            isOrderAsc: criteria?.orderBy === 'asc',
        };
    }, [criteria?.orderColumn, criteria?.orderBy]);

    const getRowHref = (row: ReminderTableRow) => {
        if (row.vehicleMaintenanceId && row.vehicleId) {
            return `/vehicles/details/${row.vehicleId}/maintenance/details/${row.vehicleMaintenanceId}`;
        }
        if (row.useContractId && row.vehicleId) {
            return `/vehicles/details/${row.vehicleId}/contract/details/use/details/${row.useContractId}`;
        }
        if (row.maintenanceContractId && row.vehicleId) {
            return `/vehicles/details/${row.vehicleId}/contract/details/maintenance/details/${row.maintenanceContractId}`;
        }
        if (row.insuranceContractId && row.vehicleId) {
            return `/vehicles/details/${row.vehicleId}/contract/details/insurance/details/${row.insuranceContractId}`;
        }
        return `reminders/details/${row.id}`;
    };

    return (
        <div className={styles.tableContainer}>
            <DataTableWithSidebar
                key={`${reRenderSearch}`}
                cacheKey={`reminder-list-v1-all`}
                columns={tableColumns}
                rows={tableRows}
                defaultHiddenColumns={['vehicleSegmentDisplay', 'vehicleLocalDisplay']}
                defaultOrder={defaultOrder}
                onRowClick={(row, _, event) => onClickItem(row, event)}
                onOrderChange={handleOrderChange}
                onRenderAction={renderTableActionCell}
                onHref={getRowHref}
                onRowId={row => row.triggerId ?? row.id ?? ''}
                scrollToRowId={scrollToRowId}
                infiniteScroll={{
                    isLoading: isLoadingMore,
                    hasMore: hasMore,
                    onLoadMore: onLoadMore,
                    hasMorePrevious: hasMorePrevious,
                    isLoadingPrevious: isLoadingPrevious,
                    onLoadPrevious: onLoadPrevious,
                }}
                exportExcel={onFetchAllForExport ? {
                    fetchAll: fetchAllForExcelExport,
                    fileName: t('reminders.title') as string,
                } : undefined}
                exportRef={exportRef}
                hideExportInSidebar={!!exportRef}
            />
            <QuestionYesNo
                onNo={onCancelRemove}
                onYes={onRemove}
                isVisible={showRemoveModal}
                message={t('common.messages.remove_charge')} />

            <ReactTooltip />
        </div>
    );
};

export default ReminderListTable;
